import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'hooks/store';
import {
  organizationResourcesItemsSelector,
  organizationResourcesRemoteTotalSelector,
  isLoadingOrganizationResourcesSelector,
  getOrganizationResourceInventoriesListAction,
  cleanOrganizationResourcesAction,
} from 'state/slices/organizations';
import { usePagination } from 'hooks/pagination';
import {
  Organization,
  PaginationData,
  Resource,
  ResourceInventorySummary,
} from 'constants/interface';
import { Search, Table } from 'UI';
import { UnitMapping } from 'constants/unit';
import { ResourceCategoryEnum } from 'constants/resource';
// import { useParams } from 'react-router';
// import { ResourceCategoryUrlMapping } from 'constants/resource';

import {
  SearchContainer,
} from './style';

interface InventoryViewProps {
  shouldReload: boolean
  onReloadFinish(): void
  activeCategory: ResourceCategoryEnum
  activeOrganization: Organization
}

const InventoryView = ({
  shouldReload,
  onReloadFinish,
  activeCategory,
  activeOrganization,
}: InventoryViewProps): JSX.Element => {
  const dispatch = useDispatch();
  const remoteTotal = useSelector(organizationResourcesRemoteTotalSelector);

  const inventoryItems = useSelector(organizationResourcesItemsSelector);
  const isRemoteLoading = useSelector(isLoadingOrganizationResourcesSelector);

  const inventories = inventoryItems.map((inventory) => {
    const tableInventory: ResourceInventorySummary & {
      key?: string,
    } = {
      ...inventory,
      key: `${inventory.resource.id.toString()}/${inventory.owner.id.toString()}`,
    };
    return tableInventory;
  }).filter((tableInventory): tableInventory is ResourceInventorySummary => !!tableInventory);

  const remoteFetch = (
    paginationData: PaginationData,
  ) => {
    const orderByMapping: Record<string, string> = {
      owner: 'organization',
    };
    const orderBy = orderByMapping[paginationData.orderBy || ''] || paginationData.orderBy;
    dispatch(getOrganizationResourceInventoriesListAction({
      ...paginationData,
      orderBy,
      category: activeCategory,
      organization: activeOrganization.id,
    }));
  };
  const pagination = usePagination<ResourceInventorySummary>(
    remoteFetch,
    {
      filter: {
        resource: undefined,
      },
    },
  );

  useEffect(() => {
    dispatch(cleanOrganizationResourcesAction());
  }, []);

  useEffect(() => {
    pagination.updateTotal(remoteTotal);
  }, [remoteTotal]);

  useEffect(() => {
    if (shouldReload) {
      pagination.fetch();
      onReloadFinish();
    }
  }, [shouldReload]);

  const sorter = (): number => 0;

  const pageData = pagination.getInternal();

  // const organizationFilter = pageData.filter.organization ?? null;

  return (
    <>
      <SearchContainer>
        <Search placeholder="Buscar no estoque" onSearch={pagination.onSearch} allowClear />
      </SearchContainer>
      <Table
        dataSource={inventories}
        pagination={pagination.getInternal()}
        loading={isRemoteLoading}
        showSorterTooltip={false}
        locale={{
          filterReset: 'Limpar',
          cancelSort: 'Clique para limpar ordenação',
          triggerAsc: 'Clique para ordem crescente',
          triggerDesc: 'Clique para ordem decrescente',
        }}
        onChange={pagination.onTableChange}
      >
        <Table.Column
          title="Item"
          key="resource"
          dataIndex="resource"
          render={(resource: Omit<
            Resource,
            'createdAt' |
            'updatedAt' |
            'createdBy' |
            'updatedBy'
            >) => resource.name}
          sorter={sorter}
          sortOrder={pageData.orderBy === 'resource' ? pageData.order : undefined}
        />
        <Table.Column
          title="Saldo atual"
          key="quantity"
          dataIndex="quantity"
          render={(quantity: number, record: ResourceInventorySummary) => {
            const formattedQuantity = Intl
              .NumberFormat(
                'pt-BR',
              ).format(quantity);
            const content = `${formattedQuantity} ${UnitMapping[record.resource.unit].name}`;
            return content;
          }}
        />
        <Table.Column
          title="Custo médio"
          key="averageCost"
          dataIndex="averageCost"
          render={(averageCost: number) => {
            const currencyValue = Intl
              .NumberFormat(
                'pt-BR',
                {
                  style: 'currency',
                  currency: 'BRL',
                },
              ).format((averageCost / 100));
            return currencyValue;
          }}
        />
      </Table>
    </>
  );
};

export default InventoryView;
